// iCalendar (RFC 5545) export of a reminder, so it can be added to the phone's calendar with an alarm.
import type { DocumentFull } from '../../../shared/types';
import { noteText } from './common';

const CRLF = '\r\n';
/** RFC 5545 §3.1: content lines SHOULD NOT be longer than 75 octets, excluding the line break. */
const MAX_LINE_OCTETS = 75;

/** Bytes a single code point occupies in UTF-8 (lone surrogates are encoded as U+FFFD, also 3 bytes). */
function utf8Length(char: string): number {
  const cp = char.codePointAt(0) ?? 0;
  if (cp < 0x80) return 1;
  if (cp < 0x800) return 2;
  if (cp < 0x10000) return 3;
  return 4;
}

/**
 * Fold a content line to at most 75 octets per physical line. Continuation lines start with a single
 * space (which counts towards the 75). Iterating by code point guarantees a multi-byte UTF-8 sequence
 * (č, š, ž, emoji) is never split across lines.
 */
export function foldIcsLine(line: string): string {
  const parts: string[] = [];
  let current = '';
  let octets = 0;
  let limit = MAX_LINE_OCTETS;
  for (const char of line) {
    const size = utf8Length(char);
    if (octets + size > limit && current) {
      parts.push(current);
      current = '';
      octets = 0;
      limit = MAX_LINE_OCTETS - 1; // room for the leading space of the continuation line
    }
    current += char;
    octets += size;
  }
  parts.push(current);
  return parts.join(`${CRLF} `);
}

/** Escape a TEXT property value (RFC 5545 §3.3.11). Control characters other than line breaks are not allowed and are dropped. */
export function escapeIcsText(value: string): string {
  return (
    value
      .normalize('NFC')
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n|\r|\n/g, '\\n')
  );
}

/** UTC DATE-TIME form "20261024T120500Z"; null for an unparsable timestamp. */
export function formatIcsDateTime(value: Date | string): string | null {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return date
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')
    .replace(/[-:]/g, '');
}

/** iCalendar (RFC 5545) VEVENT with a VALARM 10 minutes before the due time; null when the note has no dueAt. */
export function noteToIcs(doc: DocumentFull): string | null {
  if (!doc.dueAt) return null;
  const start = formatIcsDateTime(doc.dueAt);
  if (!start) return null;
  const stamp = formatIcsDateTime(new Date()) ?? start;

  const description = noteText(doc);
  const title = doc.title.trim() || description.split('\n')[0]?.trim() || 'Opomnik';
  const summary = doc.projectName ? `[${doc.projectName}] ${title}` : title;
  const lastModified = formatIcsDateTime(doc.updatedAt);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BAAM//Transkripcija//SL',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    // The document id keeps the UID stable, so re-exporting an edited reminder updates the same event.
    `UID:${doc.id}@baam-transkripcija`,
    `DTSTAMP:${stamp}`,
    ...(lastModified ? [`LAST-MODIFIED:${lastModified}`] : []),
    `DTSTART:${start}`,
    'DURATION:PT15M',
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    // A reminder should not mark the calendar as busy.
    'TRANSP:TRANSPARENT',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'TRIGGER:-PT10M',
    `DESCRIPTION:${escapeIcsText(summary)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(foldIcsLine).join(CRLF) + CRLF;
}
